/**
 * Utility to auto-format plain text responses into markdown-like structure
 * Detects patterns like numbered steps, bullet points, and key phrases
 */

export function formatPlainTextToMarkdown(text: string): string {
  if (!text) return text;

  let formatted = text;

  // First, normalize line breaks - handle both literal \n and actual newlines
  // Convert bullet points (•) to markdown list items
  // Pattern: "• text \n• text" -> "- text\n- text"
  formatted = formatted.replace(/•\s+/g, "- ");

  // Ensure newlines are preserved (convert \n escape sequences if they're literal)
  // This handles cases where \n might be stored as literal characters
  formatted = formatted.replace(/\\n/g, "\n");

  // Normalize spacing before newlines in list items
  // Pattern: "- item text  \n- next item" -> "- item text\n- next item"
  // Remove trailing spaces before newlines when followed by a list item
  formatted = formatted.replace(/(-\s+[^\n]+)\s+\n(-\s+)/g, "$1\n$2");

  // Ensure list items are on separate lines (handle cases where newline might be missing)
  formatted = formatted.replace(/(-\s+[^\n]+)\s+(-\s+)/g, "$1\n$2");

  formatted = formatted.trim();

  // Detect pattern: "To accomplish this task, you need to follow these steps: 1. ... 2. ... 3. ..."
  // Split into separate lines
  formatted = formatted.replace(
    /(\d+)\.\s+([A-Z][^.]*\.)/g,
    (match, num, content) => `\n${num}. ${content}`
  );

  // Detect sentences that start with "Based on" and make them separate paragraphs
  formatted = formatted.replace(/(Based on [^.]+\.)/g, "\n\n$1\n\n");

  // Detect introductory phrases like "To accomplish this task, you need to follow these steps:"
  formatted = formatted.replace(
    /^(To accomplish this task[^:]+:)\s*/im,
    "**$1**\n\n"
  );

  // Detect "here's the relevant information" and similar phrases
  formatted = formatted.replace(
    /(here'?s the relevant information|here is the relevant information)/gi,
    "**$1**"
  );

  // Detect and format numbered steps that might be inline
  // Pattern: "1. Do this 2. Do that 3. Do something"
  const numberedStepPattern = /\b(\d+)\.\s+([A-Z][^.]*\.)(?:\s+(\d+)\.\s+)?/g;
  let hasNumberedSteps = numberedStepPattern.test(formatted);

  if (hasNumberedSteps) {
    formatted = formatted.replace(
      /\b(\d+)\.\s+([^.]+\.)/g,
      (match, num, content, offset, string) => {
        // Check if it's already on its own line
        const beforeChar = offset > 0 ? string[offset - 1] : "\n";
        if (beforeChar === "\n") {
          return match; // Already formatted
        }
        return `\n${num}. ${content.trim()}`;
      }
    );
  }

  // Detect action verbs at sentence start and convert to bullet points
  const actionVerbs = [
    "Navigate",
    "Select",
    "Configure",
    "Save",
    "Click",
    "Open",
    "Access",
    "Choose",
    "Enter",
    "Set",
    "Enable",
    "Disable",
    "Create",
    "Delete",
    "Update",
    "Modify",
    "Review",
    "Check",
    "Verify",
    "Confirm",
    "Manage",
    "Organize",
    "Initialize",
    "Search",
    "Customize",
  ];

  const actionVerbPattern = new RegExp(
    `^(${actionVerbs.join("|")})\\s+([^.]+\\.)`,
    "gim"
  );

  formatted = formatted.replace(actionVerbPattern, (match, verb, content) => {
    // Don't convert if already in a numbered list
    if (match.trim().match(/^\d+\./)) {
      return match;
    }
    return `- ${verb} ${content}`;
  });

  // Bold section headers (sentences ending with ":" at line start)
  formatted = formatted.replace(/^([^:\n]{10,80}):(?=\s*$)/gm, "**$1:**");

  // Bold common emphatic phrases
  const emphasisPhrases = [
    "Important",
    "Note",
    "Warning",
    "Tip",
    "Remember",
    "The system",
    "This feature",
    "You can",
  ];

  emphasisPhrases.forEach((phrase) => {
    const regex = new RegExp(`\\b(${phrase})\\b(?=[^*])`, "gi");
    formatted = formatted.replace(regex, (match) => {
      // Check if already bold
      return `**${match}**`;
    });
  });

  // Clean up: remove excessive newlines (more than 2 consecutive)
  formatted = formatted.replace(/\n{3,}/g, "\n\n");

  // Clean up: trim spaces at line ends
  formatted = formatted.replace(/[ \t]+$/gm, "");

  // Ensure proper spacing after list items
  formatted = formatted.replace(/^(\d+\.\s+[^\n]+)(\n)(?=\d+\.)/gm, "$1\n");
  formatted = formatted.replace(/^(-\s+[^\n]+)(\n)(?=-\s+)/gm, "$1\n");

  return formatted.trim();
}

/**
 * Detect if text is already in markdown format
 */
export function isMarkdownFormatted(text: string): boolean {
  const markdownPatterns = [
    /^#{1,6}\s+/m, // Headers
    /\*\*[^*]+\*\*/, // Bold
    /\*[^*]+\*/, // Italic
    /^\s*[-*+]\s+/m, // Unordered lists
    /^\s*\d+\.\s+/m, // Ordered lists
    /```[\s\S]*```/, // Code blocks
    /`[^`]+`/, // Inline code
    /^\s*>\s+/m, // Blockquotes
    /\[([^\]]+)\]\(([^)]+)\)/, // Links
  ];

  // Count how many markdown patterns are present
  const matchCount = markdownPatterns.filter((pattern) =>
    pattern.test(text)
  ).length;

  // Consider it markdown if it has 2 or more markdown patterns
  return matchCount >= 2;
}
